import React, { useEffect } from "react";
import { ClassNames, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { App } from "antd5";
import { useLocation } from "wouter";

import { BID_SELF_SERVE_CHANGED } from "components/account_management/qualification-criteria/BidSelfServe";
import PaidContentBanner from "components/ui/banner/PaidContentBanner";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BidInfo } from "lib/hooks/api/bid/bids/useBid";
import { BidNoBidResponse } from "lib/hooks/api/bid/scorecard/useGetBidReport";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { useSubscription } from "lib/hooks/subscription";
import { clipboardCheck, target04 } from "lib/icons/untitled_ui/SVGs";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";
import { DetailsHeader } from "../../app_layout/DetailsLayout";
import { BidNoBidHeaderActionButtons } from "./BidNoBidHeaderActionButtons";

type Props = {
  bidId: string;
  bidInfo: BidInfo | undefined;
  data: BidNoBidResponse | undefined;
  urlShareSignature: string | null;
  slideoutOpen: boolean;
  setSlideoutOpen: (open: boolean) => void;
  onRegenerateBidReport: () => void;
};
export function BidNoBidReportHeader({
  bidId,
  bidInfo,
  data,
  urlShareSignature,
  slideoutOpen,
  setSlideoutOpen,
  onRegenerateBidReport,
}: Props) {
  const [, setLocation] = useLocation();
  const { colors } = useTheme();
  const subscription = useSubscription();
  const { logEvent } = useTracking();

  const { message } = App.useApp();

  const { mutate: actionPql } = usePqlActioned(undefined, undefined, false);

  // Show banner if the user is freemium and is here on credits
  const hasBidAccess = subscription.hasDataTypes("BIDS");

  const loggedInUser = !!window.currentUser;

  // If a user has navigated here from bid-self-serve, we must check if they've
  // changed their bid questions, if they have then we show an info toast
  useEffect(() => {
    if (sessionStorage.getItem(BID_SELF_SERVE_CHANGED)) {
      message.open({
        key: "bidSelfServeMessage",
        type: "info",
        icon: <UIcon icon="infoCircle" size={16} color={colors.sysInfoDefault} />,
        content: (
          <MessageContent>
            <Text>Report outdated, regenerate to see new criteria</Text>
            <Button
              icon="xClose"
              onClick={() => message.destroy("bidSelfServeMessage")}
              iconProps={{
                size: 16,
                color: colors.sysTextDefault,
              }}
              buttonStyle="inverse"
            />
          </MessageContent>
        ),
        duration: 0, // Stays open until manually closed
      });
      sessionStorage.removeItem(BID_SELF_SERVE_CHANGED);
    }
    // Cleanup on unmount/navigation
    return () => {
      message.destroy("bidSelfServeMessage");
      sessionStorage.removeItem(BID_SELF_SERVE_CHANGED);
    };
  }, [colors.sysInfoDefault, colors.sysTextDefault, message]);

  return (
    <>
      {!!window.currentUser && !hasBidAccess && (
        <PaidContentBanner
          paidPlanType="bidPart"
          backgroundColor={colors.sysDestructiveSubtleAlt}
          iconSize={20}
          description={
            <>
              Bid Studio is available as an add-on feature.{" "}
              <UpgradeLink
                target="new-tab"
                onClick={() => {
                  logEvent(EventNames.planUpgradeClicked, { "Context source": "Bid studio" });
                  actionPql({
                    actionType: "Upgrade",
                    dataType: "BIDS",
                    pageUrl: window.location.href,
                  });
                }}
                href="/account-management/subscription"
              >
                Upgrade
              </UpgradeLink>
            </>
          }
        />
      )}
      {/* Only show breadcrumbs if we have bidInfo returned - should show for both logged in users and non logged in users */}
      <Header showBreadcrumbs={!!bidInfo}>
        {bidInfo && (
          <>
            <BreadcrumbContainer>
              <Flex alignItems="center" gap={8}>
                <Breadcrumb
                  onClick={() => (loggedInUser ? setLocation("/") : window.open("/users/sign_in"))}
                >
                  <UIcon svg={target04} size={14} color={colors.sysPrimaryDefault} />
                  Opportunities
                </Breadcrumb>
                <Text color={colors.sysTextSecondary}> / </Text>
                <Breadcrumb
                  onClick={() =>
                    loggedInUser
                      ? setLocation(`/${bidInfo.opportunityId}`)
                      : window.open("/users/sign_in")
                  }
                >
                  <UIcon svg={target04} size={14} color={colors.sysPrimaryDefault} />
                  <ClassNames>
                    {({ css }) => (
                      <EllipsisTooltipText
                        fullText={bidInfo?.opportunity?.name || "Linked opportunity"}
                        containerClassname={css({
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          lineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          maxWidth: 250,
                        })}
                      />
                    )}
                  </ClassNames>
                </Breadcrumb>
                <Text color={colors.sysTextSecondary}> / </Text>
                <Breadcrumb inactive>
                  <UIcon svg={clipboardCheck} size={14} color={colors.sysTextSecondary} />
                  Bid / no-bid report
                </Breadcrumb>
              </Flex>
            </BreadcrumbContainer>
            <BidNoBidHeaderActionButtons
              bidId={bidId}
              bidInfo={bidInfo}
              data={data}
              urlShareSignature={urlShareSignature}
              onRegenerateBidReport={onRegenerateBidReport}
              slideoutOpen={slideoutOpen}
              setSlideoutOpen={setSlideoutOpen}
            />
          </>
        )}
      </Header>
    </>
  );
}

const Header = styled(DetailsHeader)<{ showBreadcrumbs: boolean }>(
  ({ theme, showBreadcrumbs }) => ({
    display: "flex",
    padding: "24px",
    gap: "24px",
    alignSelf: "stretch",
    borderBottom: `1px solid ${theme.colors.sysBorderPrimary}`,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: showBreadcrumbs ? "space-between" : "flex-end",
  }),
);

const UpgradeLink = styled.a(({ theme }) => ({
  fontWeight: 500,
  color: theme.colors.sysTextDefault,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    color: theme.colors.sysTextDefault,
  },
}));

const MessageContent = styled(Flex)({
  marginLeft: "8px",
  alignItems: "center",
  gap: "8px",
});

const BreadcrumbContainer = styled(Flex)({
  justifyContent: "space-between",
  padding: "0px 24px",
  alignItems: "center",
  gap: "8px",
});

const Breadcrumb = styled(Flex)<{ inactive?: boolean }>(({ theme, inactive }) => ({
  alignItems: "center",
  gap: "4px",
  cursor: inactive ? "default" : "pointer",
  color: inactive ? theme.colors.sysTextSecondary : theme.colors.sysPrimaryDefault,
  "&:hover": {
    textDecoration: inactive ? "none" : "underline",
  },
}));
